import "./style.css";
import "leaflet/dist/leaflet.css";

import type { ApiContext } from "./api";
import { ApiClient } from "./api";
import { requireCaptcha, setSitekey } from "./captcha";
import { maybeRedirectDesktop } from "./desktop_redirect";
import { decideInitialViewport, requestUserLocation } from "./geo";
import { captureBeforeInstallPrompt } from "./install";
import { registerSW } from "./sw_register";

// Capture the install prompt as soon as the module loads so we don't miss it.
// (Browsers fire `beforeinstallprompt` very early in the page lifecycle.)
captureBeforeInstallPrompt();
import { loadIdentity, setOnFallback } from "./identity";
import { createMap } from "./map";
import { openAddPanel } from "./panels/add";
import { openDetails } from "./panels/details";
import { openSettingsPanel } from "./panels/settings";
import { openVisitedPanel } from "./panels/visited";
import { wireSearch } from "./search";
import type { Visit } from "./types";

async function bootstrap(): Promise<void> {
  if (maybeRedirectDesktop()) return;

  // Production builds inject VITE_TURNSTILE_SITEKEY via the env file. Dev
  // builds (and any build that omits the var) keep the captcha module's
  // default of Cloudflare's "always passes" test sitekey.
  const envSitekey = import.meta.env.VITE_TURNSTILE_SITEKEY;
  if (typeof envSitekey === "string" && envSitekey) {
    setSitekey(envSitekey);
  }

  // Master kill-switch. When VITE_CAPTCHA_DISABLED is "true", the API client
  // skips the modal entirely and sends a placeholder token; the backend
  // (with CAPTCHA_DISABLED=true) accepts it without contacting Cloudflare.
  const captchaDisabled = import.meta.env.VITE_CAPTCHA_DISABLED === "true";

  const banner = requireEl<HTMLDivElement>("#banner");
  const emptyHint = requireEl<HTMLDivElement>("#empty-hint");
  const modalRoot = requireEl<HTMLDivElement>("#modal-root");
  const panelRoot = requireEl<HTMLDivElement>("#panel-root");
  const mapEl = requireEl<HTMLDivElement>("#map");
  const searchInput = requireEl<HTMLInputElement>("#search-input");
  const searchResults = requireEl<HTMLUListElement>("#search-results");
  const btnAdd = requireEl<HTMLButtonElement>("#btn-add");
  const btnVisited = requireEl<HTMLButtonElement>("#btn-visited");
  const btnSettings = requireEl<HTMLButtonElement>("#btn-settings");
  const btnLocate = requireEl<HTMLButtonElement>("#btn-locate");
  const btnResearch = requireEl<HTMLButtonElement>("#btn-research");

  let bannerHideTimer: number | undefined;
  const notify = (
    msg: string,
    action?: { label: string; onClick: () => void },
  ): void => {
    window.clearTimeout(bannerHideTimer);
    banner.textContent = "";
    const text = document.createElement("span");
    text.textContent = msg;
    banner.appendChild(text);
    if (action) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "banner-action";
      btn.textContent = action.label;
      btn.addEventListener("click", () => {
        banner.hidden = true;
        action.onClick();
      });
      banner.appendChild(btn);
    }
    banner.hidden = false;
    if (!action) {
      bannerHideTimer = window.setTimeout(() => {
        banner.hidden = true;
      }, 3000);
    }
  };

  setOnFallback((active) => {
    if (active) {
      notify("storage is unavailable - your identity won't persist if you reload");
    }
  });

  const identity = loadIdentity();

  let registered = false;
  const ctx: ApiContext = {
    getDeviceId: () => identity.id,
    isRegistered: () => registered,
    markRegistered: () => {
      registered = true;
    },
    getDisplayName: () => identity.name,
    getIcon: () => identity.icon,
    requireCaptchaToken: captchaDisabled
      ? () => Promise.resolve("captcha-disabled")
      : () => requireCaptcha(modalRoot),
  };
  const api = new ApiClient(ctx);

  const outcome = await decideInitialViewport();
  let initialViewport;
  let currentLocation: { lat: number; lon: number } | null = null;
  if (outcome.kind === "geolocation") {
    initialViewport = { lat: outcome.coords.lat, lon: outcome.coords.lon, zoom: outcome.zoom };
    currentLocation = outcome.coords;
  } else if (outcome.kind === "last-known") {
    initialViewport = outcome.viewport;
  } else {
    initialViewport = outcome.defaultViewport;
    notify("where are you? Use the search above.");
  }
  // Hold the user's accuracy from the latest geolocation reading so the dot
  // can be drawn with an accuracy circle.
  let currentAccuracy: number | undefined;

  const bundle = createMap({
    container: mapEl,
    api,
    initial: initialViewport,
    onError: (msg) => notify(msg),
    ownDeviceId: identity.id,
  });

  // If we already have a position from the auto-on-load geolocation, render it.
  if (currentLocation) {
    bundle.setUserLocation(currentLocation);
  }

  bundle.onSelect((t) => {
    openDetails(t, {
      api,
      panelRoot,
      currentLocation: () => currentLocation,
      onVisitedChange: () => {
        refreshVisited();
        bundle.refresh();
      },
      notify,
    });
  });

  bundle.onEmpty((empty) => {
    emptyHint.hidden = !empty;
  });

  wireSearch(api, {
    input: searchInput,
    resultsList: searchResults,
    onPick: (r) => {
      if (r.bbox) {
        const [w, s, e, n] = r.bbox;
        bundle.map.fitBounds([
          [s, w],
          [n, e],
        ]);
      } else {
        bundle.map.setView([r.lat, r.lon], 14);
      }
    },
    onError: (msg) => notify(msg),
  });

  btnAdd.addEventListener("click", () => {
    openAddPanel({
      api,
      map: bundle.map,
      panelRoot,
      notify,
      onCreated: () => {
        bundle.refresh();
      },
    });
  });

  btnVisited.addEventListener("click", () => {
    openVisitedPanel({
      api,
      panelRoot,
      notify,
      onChanged: () => refreshVisited(),
    });
  });

  btnSettings.addEventListener("click", () => {
    openSettingsPanel({ api, panelRoot, notify });
  });

  btnLocate.addEventListener("click", async () => {
    btnLocate.classList.add("locating");
    notify("locating you...");
    const result = await requestUserLocation();
    btnLocate.classList.remove("locating");
    if (result.ok) {
      currentLocation = result.coords;
      currentAccuracy = result.accuracy;
      bundle.setUserLocation(result.coords, result.accuracy);
      bundle.map.setView([result.coords.lat, result.coords.lon], 16);
      // refresh() runs on the moveend handler, no need to call manually
    } else {
      notify(result.message);
    }
  });

  btnResearch.addEventListener("click", () => {
    btnResearch.classList.add("loading");
    notify("searching this area...");
    bundle.refresh();
    window.setTimeout(() => {
      btnResearch.classList.remove("loading");
    }, 800);
  });

  // currentAccuracy is captured for future "follow my position" UX; suppress
  // the unused-variable warning by referencing it.
  void currentAccuracy;

  async function refreshVisited(): Promise<void> {
    if (!registered) {
      bundle.setVisited([]);
      return;
    }
    try {
      const visits: Visit[] = await api.getVisits();
      bundle.setVisited(visits);
    } catch {
      // silent: visited overlay is optional
    }
  }

  // Initial visited fetch only if device already registered (e.g. captcha previously cleared)
  // This is a no-op until first write. Safe to skip.

  // Register the service worker last - shouldn't block first paint or interactivity.
  registerSW({ notify });
}

function requireEl<T extends HTMLElement>(selector: string): T {
  const el = document.querySelector<T>(selector);
  if (!el) throw new Error(`element ${selector} not found`);
  return el;
}

bootstrap().catch((err) => {
  console.error(err);
  document.body.textContent = "Something went wrong loading the app.";
});
